import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Question } from "@phosphor-icons/react";
import { forwardRef, type ReactNode } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { twRing } from "@/lib/emotionTw";
import { Tooltip } from "@/components/ui/overlays/Tooltip";

export type OptionCardItem = {
  value: string;
  label: ReactNode;
  description?: ReactNode;
  helpText?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
};

export type OptionCardGroupProps = Omit<RadioGroupPrimitive.RadioGroupProps, "children"> & {
  options: OptionCardItem[];
  error?: boolean;
  columns?: 1 | 2 | 3;
};

type OptionCardColumns = 1 | 2 | 3;

interface OptionCardGroupOwnerState {
  columns: OptionCardColumns;
  error: boolean;
}

/* Exact compiled output of the pre-migration Tailwind utilities, in the same
 * class-list emission order. Verified against the frozen baseline.
 *
 * Cascade notes:
 * - error/`bfrs-border-danger` [pos 36343] and `bfrs-border-border-control`
 *   [pos 35773] are exclusive branches; both sit EARLIER in the stylesheet than
 *   hover:border-primary/70 [64622] and data-[state=checked]:border-primary
 *   [73811], so hover/checked still override the error border — modeled by
 *   keeping the base border color a plain declaration and hover/checked nested.
 * - `data-[state=checked]:bfrs-bg-primary-soft` [pos 75085] beats
 *   `hover:bfrs-bg-primary-soft/40` [pos 65447] (later in stylesheet), so the
 *   checked selector is declared after :hover here.
 * - On the description span, `bfrs-leading-5` [pos 49092] beats the line-height
 *   from `bfrs-text-xs` [pos 48728] — net line-height 1.25rem. */

const columnsStyles: Record<OptionCardColumns, CSSObject> = {
  1: {},
  2: { "@media (min-width:768px)": { gridTemplateColumns: "repeat(2,minmax(0,1fr))" } },
  3: { "@media (min-width:768px)": { gridTemplateColumns: "repeat(3,minmax(0,1fr))" } }
};

const OptionCardGroupRoot = styled(RadioGroupPrimitive.Root, {
  label: "AgenticOptionCardGroup-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: OptionCardGroupOwnerState }>(({ ownerState }) => [
  {
    display: "grid",
    gap: ".75rem",
    gridTemplateColumns: "repeat(1,minmax(0,1fr))"
  },
  columnsStyles[ownerState.columns]
]);

/* The old markup carried `bfrs-group` (no styles of its own); its only compound
 * use here — group-data-[state=checked]:bfrs-border-primary — is modeled on the
 * radio slot below via an ancestor [data-state="checked"] selector. */
const OptionCardButton = styled("button", {
  label: "AgenticOptionCardGroup-option",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: OptionCardGroupOwnerState }>(({ ownerState }) => [
  {
    display: "flex",
    minHeight: "76px",
    width: "100%",
    alignItems: "flex-start",
    gap: ".75rem",
    borderRadius: "var(--bfrs-radius-xl)",
    borderWidth: "1px",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    paddingLeft: "1rem",
    paddingRight: "1rem",
    paddingTop: ".75rem",
    paddingBottom: ".75rem",
    textAlign: "left",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    transitionProperty:
      "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s",
    "&:hover": {
      borderColor: "hsl(var(--bfrs-color-primary) / .7)",
      backgroundColor: "hsl(var(--bfrs-color-primary-soft) / .4)"
    },
    "&:focus-visible": twRing("2px", "hsl(var(--bfrs-color-border-focus) / .25)"),
    '&[data-state="checked"]': {
      "--tw-border-opacity": "1",
      borderColor: "hsl(var(--bfrs-color-primary) / var(--tw-border-opacity, 1))",
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
    },
    "&:disabled": {
      cursor: "not-allowed",
      opacity: ".55"
    }
  },
  ownerState.error
    ? {
        "--tw-border-opacity": "1",
        borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))"
      }
    : {
        "--tw-border-opacity": "1",
        borderColor: "hsl(var(--bfrs-color-form-control-border) / var(--tw-border-opacity, 1))"
      }
]);

const OptionCardIcon = styled("span", {
  label: "AgenticOptionCardGroup-icon"
})({
  marginTop: ".125rem",
  display: "flex",
  width: "2rem",
  height: "2rem",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--bfrs-radius-lg)",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
});

const OptionCardContent = styled("span", {
  label: "AgenticOptionCardGroup-content"
})({
  minWidth: "0px",
  flex: "1 1 0%"
});

const OptionCardTitleRow = styled("span", {
  label: "AgenticOptionCardGroup-titleRow"
})({
  display: "flex",
  minWidth: "0px",
  alignItems: "center",
  gap: ".375rem"
});

const OptionCardLabel = styled("span", {
  label: "AgenticOptionCardGroup-label"
})({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: ".875rem",
  lineHeight: "1.25rem",
  fontWeight: 600,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

const OptionCardHelpTrigger = styled("span", {
  label: "AgenticOptionCardGroup-helpTrigger"
})({
  display: "inline-flex",
  width: "1rem",
  height: "1rem",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--bfrs-radius-pill)",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))",
  "&:hover": {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  }
});

const OptionCardDescription = styled("span", {
  label: "AgenticOptionCardGroup-description"
})({
  marginTop: ".25rem",
  display: "block",
  fontSize: ".75rem",
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
});

const OptionCardRadio = styled("span", {
  label: "AgenticOptionCardGroup-radio"
})({
  marginTop: ".125rem",
  display: "flex",
  width: "1.25rem",
  height: "1.25rem",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--bfrs-radius-pill)",
  borderWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-form-control-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  // group-data-[state=checked]:bfrs-border-primary (data-state lives on the option button)
  '[data-state="checked"] &': {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-primary) / var(--tw-border-opacity, 1))"
  }
});

const OptionCardIndicator = styled(RadioGroupPrimitive.Indicator, {
  label: "AgenticOptionCardGroup-indicator"
})({
  width: ".625rem",
  height: ".625rem",
  borderRadius: "var(--bfrs-radius-pill)",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-primary) / var(--tw-bg-opacity, 1))"
});

export const OptionCardGroup = forwardRef<HTMLDivElement, OptionCardGroupProps>(({ className, options, error, columns = 2, ...props }, ref) => {
  const ownerState: OptionCardGroupOwnerState = { columns, error: Boolean(error) };

  return (
    <OptionCardGroupRoot ref={ref} ownerState={ownerState} className={className} aria-invalid={error} {...props}>
      {options.map((option) => (
        <RadioGroupPrimitive.Item key={option.value} value={option.value} disabled={option.disabled} asChild>
          <OptionCardButton type="button" ownerState={ownerState}>
            {option.icon && <OptionCardIcon>{option.icon}</OptionCardIcon>}
            <OptionCardContent>
              <OptionCardTitleRow>
                <OptionCardLabel>{option.label}</OptionCardLabel>
                {option.helpText && (
                  <Tooltip content={option.helpText}>
                    <OptionCardHelpTrigger>
                      <Question size={13} weight="bold" />
                    </OptionCardHelpTrigger>
                  </Tooltip>
                )}
              </OptionCardTitleRow>
              {option.description && <OptionCardDescription>{option.description}</OptionCardDescription>}
            </OptionCardContent>
            <OptionCardRadio>
              <OptionCardIndicator />
            </OptionCardRadio>
          </OptionCardButton>
        </RadioGroupPrimitive.Item>
      ))}
    </OptionCardGroupRoot>
  );
});

OptionCardGroup.displayName = "OptionCardGroup";
